import _ from "lodash";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import BuildProgramStepOneModal from "../../../parts/modals/add-cycle";
import AddExerciseModal from "../../../parts/modals/add-exercise";
import DuplicateWeekModal from "../../../parts/modals/duplicate-week";
import DuplicateWorkoutModal from "../../../parts/modals/duplicate-workout";
import ImportWorkoutModal from "../../../parts/modals/import-workout";
import ProgressionModal from "../../../parts/modals/progression";
import SwapExerciseModal from "../../../parts/modals/swap-exercise";
import { previousAuthoredWeek } from "../../../utils/flexible-program";
import ProgramEditor from "./ProgramEditor";

const ProgramEditorController = async ({ res, req, locals, load, flash }) => {
  const { query } = req;
  let editExercise;
  const modal = _.get(query, "modal", {});

  // Templates only. The editor resolves exercises by the ids in
  // programs.sessions.exercises, so scheduled clones sharing this cycle.id are
  // never looked up - they are just payload. On a flexible program (one
  // document, coach authors + client claims into it) that payload is every
  // workout the client ever scheduled. `$ne: true` rather than `false` so
  // legacy templates that predate the field are still returned.
  const templateOnly = { $ne: true };

  const editExercisesOptions = locals.activeUser.organizationId
    ? {
        cycle: req.params.cycleId,
        proxyAthlete: query.athlete,
        program: query.program,
        forProgramEditor: true,
        isScheduled: templateOnly,
        organizationCoach: locals.activeUserToRequest._id,
      }
    : {
        cycle: req.params.cycleId,
        proxyAthlete: query.athlete,
        program: query.program,
        forProgramEditor: true,
        isScheduled: templateOnly,
      };
  const {
    activeUserToRequest: {
      exerciseLibrary: { assistant },
    },
  } = locals;

  let queryEditCycle = {
    cycle: req.params.cycleId,
    proxyAthlete: query.athlete,
  };

  if (query.storeType === "flexible" && query.athlete) {
    // Open to every coach in the organization, whoever created the program.
    queryEditCycle = { ...queryEditCycle, flexibleAthlete: query.athlete };
  } else if (query.athlete && locals.activeUser.organizationId) {
    queryEditCycle = {
      ...queryEditCycle,
      athleteFromOrganization: query.athlete,
    };
  }

  const loadPromises = [
    load("editCycle", queryEditCycle),
    load("editExercises", editExercisesOptions),
    load("editSections", {
      cycle: req.params.cycleId,
      proxyAthlete: query.athlete,
      ...(locals.activeUser.organizationId && {
        organizationCoach: locals.activeUserToRequest._id,
      }),
    }),
  ];

  if (query.athlete) {
    loadPromises.push(
      load("clientConfiguration", { proxyAthlete: query.athlete })
    );
  }

  const isAllowToViewAllOrganizationsAthletes =
    locals.organizationRoleName &&
    (locals.organizationRoleName === "Owner" ||
      locals.organizationRoleName === "Admin");

  if (
    locals.activeUser.organizationId &&
    isAllowToViewAllOrganizationsAthletes &&
    !locals.allAthletesFromOrganization
  ) {
    loadPromises.push(load("allAthletesFromOrganization"));
  }

  if (modal?.type === "add-exercise" && query.addType === "complex") {
    loadPromises.push(load("complexes"));
  }

  if (modal?.type === "import-workout") {
    loadPromises.push(load("savedWorkouts"));
    loadPromises.push(load("workoutFolders"));
  }

  await Promise.all(loadPromises);

  editExercise = query.editMode
    ? _.find(locals.editExercises, { _id: query.exercise })
    : {};

  const programOrder = query.program || 1;

  const programKey =
    _.findKey(locals.editCycle.programs, {
      order: programOrder,
    }) || locals.editCycle.programs.length - 1;

  locals.program = locals.editCycle.programs[programKey];

  locals.activeWeek = query.week || 1;

  locals.weeks = _.groupBy(locals.program.sessions, "week");
  locals.weeksExercises = {};
  locals.heading = ["Athlete", "Programs", locals.program.name];

  _.map(
    locals.weeks,
    (week, order) =>
      (locals.weeksExercises[order] = week.reduce(
        (arr, session) => arr.concat(session.exercises),
        []
      ))
  );

  // Teams and flexible programs are both authored a week at a time, so both
  // get the side-by-side previous-week column. Flexible orders are not
  // contiguous (deleted and replaced weeks keep theirs), so its previous week
  // is the authored week before this one by start date, never `order - 1`.
  const isFlexibleStore = query.storeType === "flexible";
  const previousProgram = isFlexibleStore
    ? previousAuthoredWeek(locals.editCycle, locals.program.order)
    : query.storeType === "teams" && query.program > 1
      ? locals.editCycle.programs[programKey - 1]
      : null;

  if (previousProgram) {
    const previousOrder = isFlexibleStore
      ? previousProgram.order
      : query.program - 1;

    const queryPreviousProgram = {
      program: previousOrder,
      cycleId: req.params.cycleId,
    };

    const editPreviousExerciseOptions = locals.activeUser.organizationId
      ? {
          cycle: req.params.cycleId,
          proxyAthlete: query.athlete,
          program: previousOrder,
          forProgramEditor: true,
          organizationCoach: locals.activeUserToRequest._id,
        }
      : {
          cycle: req.params.cycleId,
          proxyAthlete: query.athlete,
          program: previousOrder,
          forProgramEditor: true,
        };

    await load("previousProgramExercises", queryPreviousProgram);
    await load("editPreviousExercises", editPreviousExerciseOptions);

    locals.previousProgram = previousProgram;

    locals.previousActiveWeek = query.week || 1;
    locals.previousWeeks = _.groupBy(locals.previousProgram.sessions, "week");
    locals.previousWeeksExercises = {};

    _.map(
      locals.previousWeeks,
      (week, order) =>
        (locals.weeksExercises[order] = week.reduce(
          (arr, session) => arr.concat(session.exercises),
          []
        ))
    );
  }

  // Done at the end of a long day. TODO: refactor
  // Pass an array to DuplicateWeekModal to check if a week already has exercises
  // locals.weeks takes on a weird form because the Program
  // Settings modal save button alters the exercises array in the sessions
  const duplicateCheck = [];
  for (const week in locals.weeks) {
    let hasExercises = false;
    locals.weeks[week].map((session) => {
      if (session.exercises && session.exercises.length) {
        hasExercises = true;
      }
    });
    duplicateCheck.push(hasExercises);
  }

  const sessions = _.groupBy(locals.program.sessions, "order");

  locals.modal = {
    "add-exercise": (
      <AddExerciseModal
        editExercise={editExercise}
        lengthAssistantLibrary={assistant.length}
        secondaryAdded={query.secondaryAdded}
      />
    ),
    "duplicate-workout": (
      <DuplicateWorkoutModal
        sessions={sessions}
        weeksOrder={_.keys(locals.weeks)}
      />
    ),
    "duplicate-week": (
      <DuplicateWeekModal
        weeks={locals.weeks}
        duplicateCheck={duplicateCheck}
        weeksOrder={_.keys(locals.weeks)}
      />
    ),
    "swap-exercise": (
      <SwapExerciseModal
        exercise={editExercise}
        isProgramBuilder
        programNumber={programOrder}
        cycleId={req.params.cycleId}
        cycleOwner={query.athlete || locals.editCycle.owner}
      />
    ),
    "import-workout": (
      <ImportWorkoutModal
        savedWorkouts={locals.savedWorkouts || []}
        workoutFolders={locals.workoutFolders || []}
        cycleId={req.params.cycleId}
        programNumber={programOrder}
        programWeek={query.programWeek}
        programSession={query.programSession}
        exercisesLength={query.exercisesLength}
        highestOrder={query.highestOrder}
        athlete={locals.athlete || locals.activeUserToRequest}
      />
    ),
    "program-settings": <BuildProgramStepOneModal />,
    progression: <ProgressionModal />,
    "activity-feed": <ActivityDrawer />,
  }[modal.type];

  res.body = <ProgramEditor exercises={locals.editExercises} />;
};

export default ProgramEditorController;
